import { CommonModule } from '@angular/common';
import { Component, computed, HostListener, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { map } from 'rxjs';
import { OmniInsightsTitleService } from '../../../services/omni-insights-title/omni-insights-title.service';
import { ReportsService } from '../../../services/reports/reports.service';
import { DisplayService } from '../../../services/reports/display.service';
import { BaseComponent } from '../../base/base.component';
import { BackButtonComponent } from '../../core/buttons/back-button.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { StepComponent } from '../../core/stepper/step.component';
import { StepperComponent } from '../../core/stepper/stepper.component';
import { FilterReportComponent } from './steps/filter-report-step.component';
import { ReportDetailStepComponent } from './steps/report-detail-step.component';
import { ReportScheduleStepComponent } from './steps/report-schedule-step.component';
import { SelectReportStepComponent } from './steps/select-report-step.component';
import { HomeCardComponent } from '../../home/home-card.component';

@Component({
  selector: 'app-edit-report',
  imports: [
    StepperComponent,
    StepComponent,
    SelectReportStepComponent,
    FilterReportComponent,
    ReportScheduleStepComponent,
    ReportDetailStepComponent,
    CommonModule,
    RouterModule,
    PageHeaderComponent,
    BackButtonComponent,
    HomeCardComponent,
  ],
  template: `
    <app-page-header
      [label]="pageTitle"
      headerSize="h1"
      paddingClasses="tw-pb-2"
    />
    <app-back-button (click)="confirmCancel()">Cancel</app-back-button>

    <div
      class="tw-grow tw-justify-center tw-w-full edit-report-flow tw-min-w-0"
      [class.is-configure-step]="currentStepName() === 'configure'"
    >
      <div
        class="edit-report-card-gutter tw-min-w-0 tw-pt-8 tw-mb-4 tw-box-border"
      >
        <app-home-card
          type="Large"
          overflow="visible"
          classes="edit-report-home-card-expand"
        >
          <div
            class="edit-report-stepper-shell tw-min-w-0 tw-py-8 tw-px-2 sm:tw-px-4 md:tw-px-8 tw-box-border"
          >
            @if (isDeliveryOnlyMode()) {
              <app-report-schedule-step [isDeliveryOnly]="true" />
            } @else {
              <app-stepper #stepper [preserveSteps]="true">
                <app-step label="Report" icon="fa-light fa-file" name="report">
                  <ng-template>
                    <app-select-report-step [stepper]="stepper" />
                  </ng-template>
                </app-step>
                <app-step
                  label="Configure"
                  icon="fa-light fa-file-pen"
                  name="configure"
                >
                  <ng-template>
                    <app-report-detail-step [stepper]="stepper" />
                  </ng-template>
                </app-step>
                @if (display.showFilterStep()) {
                  <app-step label="Filter" icon="fa-filter" name="filter">
                    <ng-template>
                      <app-filter-report-step [stepper]="stepper" />
                    </ng-template>
                  </app-step>
                }
                <app-step label="Delivery" icon="fa-envelope" name="delivery">
                  <ng-template>
                    <app-report-schedule-step [stepper]="stepper" />
                  </ng-template>
                </app-step>
              </app-stepper>
            }
          </div>
        </app-home-card>
      </div>
    </div>
  `,
  styles: [
    `
      :host ::ng-deep h2 {
        font-size: 20px;
        font-weight: normal;
      }

      /* Default (all steps): card stays within the viewport. */
      .edit-report-flow {
        max-width: 100%;
        min-width: 0;
      }

      .edit-report-card-gutter,
      .edit-report-stepper-shell,
      :host ::ng-deep .edit-report-home-card-expand,
      :host ::ng-deep .edit-report-home-card-expand > div {
        box-sizing: border-box;
        width: 100%;
        max-width: 100%;
        min-width: 0;
      }

      /* Configure step on tablet: keep card within viewport; wide controls
         (dual-list, pick-list, field tiles) flex or scroll inside the card.
         Scoped to .is-configure-step so other steps keep the default layout. */
      @media (min-width: 640px) and (max-width: 1023px) {
        .edit-report-flow.is-configure-step {
          min-width: 0;
        }

        .is-configure-step .edit-report-card-gutter,
        .is-configure-step .edit-report-stepper-shell,
        :host ::ng-deep .is-configure-step .edit-report-home-card-expand,
        :host
          ::ng-deep
          .is-configure-step
          .edit-report-home-card-expand
          > div {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
        }

        :host ::ng-deep .is-configure-step app-stepper > :first-child {
          width: 100%;
          min-width: 0;
          max-width: 100%;
        }
      }
    `,
  ],
})
export class EditReportPageComponent extends BaseComponent implements OnInit {
  protected reports = inject(ReportsService);
  display = inject(DisplayService);
  private readonly pageTitleService = inject(OmniInsightsTitleService);

  isDeliveryOnlyMode = computed(
    () =>
      this.reports.isEditScheduledDeliveryMode() ||
      this.reports.isSchedulePersonalizedMode(),
  );

  /**
   * Current step name, derived from the `?step=` query param that the
   * stepper writes on every navigation. Used to scope the tablet layout
   * CSS to the Configure step only (see styles above).
   */
  protected readonly currentStepName = toSignal(
    this.activatedRoute.queryParamMap.pipe(map((qp) => qp.get('step') ?? '')),
    { initialValue: '' },
  );

  override get pageTitle(): string {
    if (this.reports.isEditScheduledDeliveryMode())
      return 'Edit delivery details';
    if (this.reports.isSchedulePersonalizedMode()) return 'New schedule';
    if (this.reports.isEditPersonalizedMode()) return 'Edit report';
    return 'New report';
  }

  ngOnInit(): void {
    if (this.isDeliveryOnlyMode()) {
      this.pageTitleService.setSubmenu('Delivery');
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: Event) {
    this.reports.storeCurrentReport();
  }

  confirmCancel() {
    const isDeliveryOnly = this.isDeliveryOnlyMode();
    const isEditPersonalized = this.reports.isEditPersonalizedMode();
    const isEditing = isDeliveryOnly || isEditPersonalized;

    this.confirm.show({
      label: isEditing ? 'Cancel edit' : 'Cancel report',
      message: isEditing
        ? 'Are you sure you want to cancel this edit? Any unsaved changes will be discarded.'
        : 'Are you sure you want to leave? Any unsaved changes will be discarded.',
      width: '35rem',
      confirm: () => {
        this.reports.clearReport();
        this.reports.clearMode();

        const tab = isDeliveryOnly ? 'scheduled' : 'reports';
        this.router.navigate(['reports'], { queryParams: { tab } });
      },
    });
  }
}
